import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Cpu, 
  Database, 
  Globe, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Zap,
  Server,
  Monitor,
  AlertTriangle,
  BarChart3
} from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { useProcessingLogs } from '@/hooks/useProcessingLogs';
import { AdvancedPerformanceAnalytics } from '@/components/AdvancedPerformanceAnalytics';

interface SystemMetric {
  name: string;
  value: number;
  unit: string;
  status: 'normal' | 'warning' | 'critical';
  trend: 'up' | 'down' | 'stable';
  history: Array<{ time: string; value: number }>;
}

interface ProcessingEvent {
  id: string;
  timestamp: Date;
  type: 'processing_start' | 'processing_complete' | 'error' | 'warning';
  message: string;
  billId?: string;
  processingTime?: number;
  status: 'success' | 'error' | 'warning';
}

export const RealTimeMonitor = () => {
  // Enhanced version available - redirect to advanced performance analytics
  return <AdvancedPerformanceAnalytics />;
};

const LegacyRealTimeMonitor = () => {
  const { logs } = useProcessingLogs();
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Generate real-time system metrics
  const [systemMetrics, setSystemMetrics] = useState<SystemMetric[]>([
    {
      name: 'CPU Usage',
      value: 45,
      unit: '%',
      status: 'normal',
      trend: 'stable',
      history: []
    },
    {
      name: 'Memory Usage',
      value: 62,
      unit: '%',
      status: 'normal',
      trend: 'up',
      history: []
    },
    {
      name: 'Processing Queue',
      value: 12,
      unit: 'bills',
      status: 'normal',
      trend: 'down',
      history: []
    },
    {
      name: 'API Response Time',
      value: 245,
      unit: 'ms',
      status: 'normal',
      trend: 'stable',
      history: []
    },
    {
      name: 'Database Connections',
      value: 8,
      unit: 'active',
      status: 'normal',
      trend: 'stable',
      history: []
    },
    {
      name: 'Error Rate',
      value: 2.1,
      unit: '%',
      status: 'normal',
      trend: 'down',
      history: []
    }
  ]);

  // Generate real-time processing events
  const [processingEvents, setProcessingEvents] = useState<ProcessingEvent[]>([
    {
      id: '1',
      timestamp: new Date(Date.now() - 30000),
      type: 'processing_complete',
      message: 'Bill processed successfully - Electric bill from ConEd',
      billId: 'bill_123',
      processingTime: 2300,
      status: 'success'
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 45000),
      type: 'processing_start',
      message: 'Starting processing for Gas bill upload',
      billId: 'bill_124',
      status: 'success'
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 62000),
      type: 'warning',
      message: 'Low confidence score detected - manual review recommended',
      billId: 'bill_122',
      status: 'warning'
    },
    {
      id: '4',
      timestamp: new Date(Date.now() - 85000),
      type: 'error',
      message: 'OCR extraction failed - file format not supported',
      billId: 'bill_121',
      status: 'error'
    }
  ]);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemMetrics(prev => prev.map(metric => {
        const variation = (Math.random() - 0.5) * 10;
        const newValue = Math.max(0, metric.value + variation);
        const newHistory = [...metric.history.slice(-19), {
          time: new Date().toLocaleTimeString(),
          value: newValue
        }];

        return {
          ...metric,
          value: newValue,
          history: newHistory,
          status: newValue > 80 ? 'critical' : newValue > 60 ? 'warning' : 'normal',
          trend: variation > 2 ? 'up' : variation < -2 ? 'down' : 'stable'
        };
      }));

      // Occasionally add new processing events
      if (Math.random() > 0.7) {
        const eventTypes: ProcessingEvent['type'][] = ['processing_start', 'processing_complete', 'warning', 'error'];
        const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
        const newEvent: ProcessingEvent = {
          id: Math.random().toString(36).substr(2, 9),
          timestamp: new Date(),
          type: eventType,
          message: `${eventType.replace('_', ' ')} - ${Math.random().toString(36).substr(2, 9)}`,
          billId: `bill_${Math.floor(Math.random() * 1000)}`,
          processingTime: eventType === 'processing_complete' ? Math.floor(Math.random() * 5000) + 1000 : undefined,
          status: eventType === 'error' ? 'error' : eventType === 'warning' ? 'warning' : 'success'
        };

        setProcessingEvents(prev => [newEvent, ...prev.slice(0, 19)]);
      }

      setLastUpdate(new Date());
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Activity className="h-4 w-4 text-blue-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'critical': return 'destructive';
      case 'warning': return 'default';
      case 'normal': return 'secondary';
      default: return 'secondary';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-3 w-3 text-red-500" />;
      case 'down': return <TrendingDown className="h-3 w-3 text-green-500" />;
      default: return <Activity className="h-3 w-3 text-gray-500" />;
    }
  };

  const chartConfig = {
    value: {
      label: "Value",
      color: "hsl(var(--chart-1))",
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Monitor className="h-6 w-6 text-primary" />
            Real-Time Monitor
          </h2>
          <p className="text-muted-foreground">
            Live system metrics and processing activity
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
            <span className="text-sm text-muted-foreground">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            Last update: {lastUpdate.toLocaleTimeString()}
          </div>
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {systemMetrics.map((metric) => (
          <Card key={metric.name}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                {metric.name}
              </CardTitle>
              <div className="flex items-center gap-1">
                {getTrendIcon(metric.trend)}
                <Badge variant={getStatusBadge(metric.status)} className="text-xs">
                  {metric.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold">
                {(metric.value || 0).toFixed(metric.name.includes('Rate') ? 1 : 0)} {metric.unit}
              </div>
              {metric.status === 'critical' && (
                <Progress value={metric.value} className="mt-2 h-1" />
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Monitoring Dashboard */}
      <Tabs defaultValue="activity" className="space-y-4">
        <TabsList>
          <TabsTrigger value="activity">Live Activity</TabsTrigger>
          <TabsTrigger value="metrics">System Metrics</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Processing Events</CardTitle>
                <CardDescription>Real-time processing activity and status updates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {processingEvents.map((event) => (
                    <div key={event.id} className="flex items-start gap-3 p-3 rounded-lg border">
                      {getStatusIcon(event.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{event.message}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span>{event.timestamp.toLocaleTimeString()}</span>
                          {event.billId && <span>ID: {event.billId}</span>}
                          {event.processingTime && <span>{event.processingTime}ms</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Processing Statistics</CardTitle>
                <CardDescription>Current processing metrics and trends</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-green-600">127</div>
                    <div className="text-xs text-muted-foreground">Bills Processed Today</div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-blue-600">12</div>
                    <div className="text-xs text-muted-foreground">Currently Processing</div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-amber-600">3</div>
                    <div className="text-xs text-muted-foreground">Pending Review</div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <div className="text-2xl font-bold text-red-600">2</div>
                    <div className="text-xs text-muted-foreground">Processing Errors</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Success Rate</span>
                    <div className="flex items-center gap-2">
                      <Progress value={97.8} className="w-20 h-2" />
                      <span className="text-sm font-medium">97.8%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Avg Processing Time</span>
                    <span className="text-sm font-medium">2.3s</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Queue Wait Time</span>
                    <span className="text-sm font-medium">0.8s</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {systemMetrics.slice(0, 4).map((metric) => (
              <Card key={metric.name}>
                <CardHeader>
                  <CardTitle>{metric.name}</CardTitle>
                  <CardDescription>Real-time {metric.name.toLowerCase()} monitoring</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl font-bold">
                      {(metric.value || 0).toFixed(1)} {metric.unit}
                    </div>
                    <Badge variant={getStatusBadge(metric.status)}>
                      {metric.status}
                    </Badge>
                  </div>
                  <ChartContainer config={chartConfig} className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={metric.history}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke={chartConfig.value.color}
                          fill={chartConfig.value.color}
                          fillOpacity={0.4}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Alerts</CardTitle>
              <CardDescription>Critical alerts and warnings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 dark:text-red-100">High Error Rate Detected</h4>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        Processing error rate has increased to 5.2% in the last hour. 
                        Manual review recommended for recent uploads.
                      </p>
                      <div className="text-xs text-red-600 dark:text-red-400 mt-2">
                        Triggered 5 minutes ago
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-900 dark:text-amber-100">Queue Backup Warning</h4>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        Processing queue has 25+ items waiting. Consider scaling up processing capacity.
                      </p>
                      <div className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        Triggered 12 minutes ago
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                  <div className="flex items-start gap-3">
                    <Activity className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100">Model Performance Improved</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        AI extraction accuracy improved to 96.2% after recent training update.
                      </p>
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                        Triggered 1 hour ago
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Analytics</CardTitle>
              <CardDescription>System performance trends and optimization insights</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Server className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Performance Dashboard</h3>
                <p className="text-muted-foreground mb-4">
                  Detailed performance analytics and optimization recommendations
                </p>
                <Button>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Detailed Analytics
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};